import { Form, Input, Modal, Divider, TreeSelect } from "antd";
import { useForm } from "antd/lib/form/Form";
import { DefaultOptionType } from "antd/lib/select";
import React, { useEffect } from "react";
import { GroupedEntityType } from "~/gql/graphql";
import { CreateGroupItemModal } from "../groups/create-group-item-modal";
import { useGroupItems } from "../groups/hooks/use-group-items";

type CreateModalProps = {
  visible: boolean;
  onCreate: (values: FormValues) => void;
  onEdit: (values: FormValues) => void;
  onCancel: () => void;
  validateTitle: (title: string) => boolean;
  onAddNewGroupItem: () => void;
  groupedEntityType: GroupedEntityType;
  isEditMode?: boolean;
  initialValues?: FormValues | null;
};

export type FormValues = {
  title: string;
  description: string;
  groupItemId: number;
};

type FormRawValues = {
  title: string;
  description: string;
  groupItemId: number;
};

type GroupItem = {
  id: number;
  name: string;
  parentId?: number | null;
  children?: GroupItem[] | null;
};

export const mapGroupItemToCascade = (items: GroupItem[]): DefaultOptionType[] => {
  return items.map((item) => ({
    value: item.id,
    label: item.name,
    children: mapGroupItemToCascade(item.children || []),
  }));
};

export const CreateModal: React.FC<CreateModalProps> = ({
  visible,
  onCreate,
  onEdit,
  onCancel,
  validateTitle,
  groupedEntityType,
  isEditMode = false,
  initialValues,
}) => {
  const [form] = useForm<FormRawValues>();
  const { data } = useGroupItems(groupedEntityType);

  // Set initial values if in edit mode
  useEffect(() => {
    if (isEditMode && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [isEditMode, initialValues, form]);

  return (
    <>
      <Modal
        width={800}
        open={visible}
        title={isEditMode ? "Edit template" : "Create a new template"}
        onOk={() =>
          form
            .validateFields()
            .then((vals) => {
              form.resetFields();
              const formValues: FormValues = {
                title: vals.title,
                description: vals.description,
                groupItemId: vals.groupItemId,
              };
              if (isEditMode) {
                onEdit(formValues);
              } else {
                onCreate(formValues);
              }
            })
            .catch(() => {})
        }
        onCancel={onCancel}
        okText={isEditMode ? "Save Changes" : "Create"}
      >
        <Form form={form} layout="vertical" name="form_in_modal">
          <Form.Item
            rules={[
              {
                validator(rule, value, callback) {
                  if (!validateTitle(value)) {
                    callback();
                  } else {
                    callback("Title already exists");
                  }
                },
              },
            ]}
            name="title"
            label="Title"
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item name="groupItemId" label="Label">
            <TreeSelect
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ display: "flex", margin: "0.25rem" }}>
                    <CreateGroupItemModal
                      minimal={true}
                      groupItemEntityType={groupedEntityType}
                      setModalState={() => {}}
                    />
                  </div>
                </>
              )}
              treeData={mapGroupItemToCascade(data?.groupItems ?? [])}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
